/**
 * The order the top-centre status banners appear in.
 *
 * Three independent features each grew a banner pinned to the top centre of the screen, none of
 * them aware of the others: audio playback blocked (SC-257/SC-304), connection lost, and controls
 * pushed off-screen by zoom (SC-255). Any two showing at once landed on the same pixels and the
 * highest z-index simply won.
 *
 * They now render as siblings in one flex column (`components/StatusBannerViewport`), so stacking
 * is the browser's job and all this has to decide is sequence. Mount order cannot be relied on —
 * the banners live in unrelated subtrees, two arrive through lazy chunks, and all three reach the
 * column through portals — so sequence is carried explicitly as a CSS `order`.
 */

/**
 * Top to bottom. Earlier means higher priority: it keeps the top slot and the others sit below.
 *
 * The order is by how much the message costs the user to miss. Blocked audio is a dead room until
 * they click, a dropped connection is next, and being zoomed in is a cosmetic annoyance they can
 * already see for themselves.
 */
export const BANNER_ORDER = ['audio-resume', 'connection', 'zoom'] as const

export type BannerId = (typeof BANNER_ORDER)[number]

/**
 * The CSS `order` for a banner, so screen position follows `BANNER_ORDER` rather than whichever
 * portal happened to commit first.
 */
export function bannerFlexOrder(id: BannerId): number {
  return BANNER_ORDER.indexOf(id)
}
